import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { buildLocalizedPath } from '@/utils/localeSlug';
import { useTranslation } from 'react-i18next';

// Utils
import cn from '@/utils/cn';

// Components
import Button from '@/components/atoms/Button/Button';

// Other 
import { type HeaderType } from '@/services/header/headerService';

// Styles
import ChevronDownLightIcon from '@/assets/icons/chevron-down-light.svg?react';
import MenuIcon from '@/assets/icons/menu.svg?react';
import ProfileIcon from '@/assets/icons/profil.svg?react';
import CloseIcon from '@/assets/icons/close.svg?react';
import LanguageIcon from '@/assets/icons/language.svg?react';
import type { resources } from '@/locales/resources';
import { getLocaleFlag, supportedLocales } from '@/utils/localeFlags';
import styles from './BurgerMenu.module.css';

interface BurgerMenuProps {
  logo: HeaderType['logoMobile'],
  menu: HeaderType['menu'],
  loginButton: HeaderType['loginButton'],
  registerButton: HeaderType['registerButton'],
  support: HeaderType['support'],
}

function BurgerMenu({
  logo,
  menu,
  loginButton,
  registerButton,
  support,
  
}: BurgerMenuProps) {
  const { getLng, changeLng, localizedPath } = useI18n();
  const { t } = useTranslation();

  const [dropdownStates, setDropdownStates] = useState<{[key: number]: boolean}>({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownLanguage, setIsDropdownLanguage] = useState(false);


  const toggleDropdown = (index: number) => {
    setDropdownStates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleLanguageChange = (lng: keyof typeof resources) => {
    changeLng(lng);
    setIsDropdownLanguage(false);
  };

  return (
    <>
      <div>
        <button
          type='button'
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {
            isMenuOpen ? <CloseIcon /> : <MenuIcon />
          }
        </button>
      </div>
      <Link to={buildLocalizedPath('', getLng())}>
        <img src={logo.url} alt={logo.alternativeText || 'Pneumaclic logo' }/>
      </Link>
      <div className={styles['header-actions']}>
        <Link to={loginButton.url}>
          <ProfileIcon />
        </Link>
        <div>
          <button type='button' className={styles.flag} onClick={() => setIsDropdownLanguage(!isDropdownLanguage)}>
            <LanguageIcon width={25} />
          </button>
          <div className={cn([
            styles['burger-menu'],
            isDropdownLanguage && styles.open
          ])}>
            <div className={styles.nav}>
              {supportedLocales.map((locale) => {
                const FlagIcon = getLocaleFlag(locale);

                return (
                  <button
                    key={locale}
                    type='button'
                    className={styles.flag}
                    onClick={() => handleLanguageChange(locale)}
                  >
                    <FlagIcon className={styles['flag-icon']} />
                    {t(`languages.${locale}`)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div
        className={`
          ${styles['burger-menu']}
          ${isMenuOpen ? styles.open : ''}
        `}
      >
        <nav className={styles.nav}>
          {
            menu.map((item, index) => (
              item.linkType === 'Link' ? (
                <Link key={item.link.title} to={localizedPath(item.link.url)} className={styles['nav-item']}>
                  {item.link.title}
                </Link>
              ) : (
                <div key={item.link.title} >
                  <div className={`${styles['nav-item']} ${styles.dropdown}`}>
                    <Link to={localizedPath(item.link.url)}>
                      {item.link.title}
                    </Link>
                    <button
                      type="button"
                      onClick={() => toggleDropdown(index)}
                      className={`${styles['dropdown-icon']} ${dropdownStates[index] ? styles.open : '' }`}
                    >
                      <ChevronDownLightIcon />
                    </button>
                  </div>
                  {
                    dropdownStates[index] && item.dropdown ? (
                      <div className={styles['dropdown-content']}>
                        {
                          item.dropdown.map((link) => (
                            <Link key={link.title} to={localizedPath(link.url)} className={styles['dropdown-nav-item']}>
                              {link.title}
                            </Link>
                          ))
                        }
                      </div>
                    ) : null
                  }
                </div>
              )
            ))
          }
        </nav>
        <div className={styles.buttons}>
          <Button
            title={registerButton.title}
            bgColor='red'
            url={registerButton.url}
          />
          <Button
            title={loginButton.title}
            bgColor='white'
            url={loginButton.url}
          />
        </div>
        {
          support ? (
            <>
              <div className={styles.line} />
              <div className={styles.footer}>
                <h4 className={styles['footer-title']}>
                  {support.title}
                </h4>
                <nav className={styles['footer-nav']}>
                  {
                    support.links.map((link) => (
                      <Link key={link.url} to={localizedPath(link.url)} className={styles['footer-nav-item']}>
                        { link.icon ? <img src={link.icon.url} alt={link.icon.alternativeText || ''} /> : null }
                        <span>{link.title}</span>
                      </Link>
                    ))
                  }
                </nav>
              </div>
            </>
          ) : null
        }
      </div>
    </>
  );
}

export default BurgerMenu;